/**
 * Torque, rotational equilibrium and moment of inertia.
 *
 *   τ = r F sin θ            torque of a force F applied at distance r from the pivot, θ
 *                            being the angle between the lever arm r and the force
 *   Στ = 0                   rotational equilibrium (a balanced lever or seesaw)
 *   I = Σ m r²               moment of inertia, with standard results for common shapes
 *   α = τ / I                angular acceleration
 *
 * OpenStax University Physics Vol. 1, §10.6 (torque), §10.5 (moments of inertia and the
 * parallel-axis theorem), §12.1 (conditions for equilibrium). Counter-clockwise torques
 * are counted positive and clockwise ones negative.
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ANGLE,
  FORCE,
  LENGTH,
  TORQUE,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

/** Smallest |sin θ| treated as non-zero: sin 180° is 1.2e-16 in floating point, not 0. */
const SIN_EPSILON = 1e-12;

export type TorqueKey = "t" | "r" | "F" | "a";

export const TORQUE_RELATION: RelationDef<TorqueKey> = {
  keys: ["t", "r", "F", "a"],
  symbols: { t: "τ", r: "r", F: "F", a: "θ" },
  families: { t: TORQUE, r: LENGTH, F: FORCE, a: ANGLE },
  equation: "τ = r × F × sin(θ)",
  solvedFor: "t",
  rearranged: {
    t: "τ = r × F × sin(θ)",
    r: "r = τ ÷ (F × sin(θ))",
    F: "F = τ ÷ (r × sin(θ))",
    a: "θ = arcsin(τ ÷ (r × F))",
  },
  workingUnit: { a: "deg" },
  solve(unknown, { t, r, F, a }) {
    if (unknown !== "r" && r < 0) {
      return fail("distance-negative");
    }
    if (unknown !== "F" && F < 0) {
      return fail("force-negative");
    }
    if (unknown !== "t" && t < 0) {
      return fail("torque-negative");
    }
    const sin = Math.sin(a);
    if (unknown === "t") {
      return r * F * sin;
    }
    if (unknown === "a") {
      if (!(r > 0 && F > 0)) {
        return fail("arm-force-zero");
      }
      const ratio = t / (r * F);
      return ratio > 1 + 1e-12
        ? fail("ratio-out-of-range")
        : Math.asin(Math.min(1, ratio));
    }
    if (Math.abs(sin) < SIN_EPSILON) {
      return fail("angle-parallel");
    }
    const other = unknown === "r" ? F : r;
    if (!(other > 0)) {
      return fail(unknown === "r" ? "force-zero" : "distance-zero");
    }
    const value = t / (other * sin);
    return value < 0 ? fail("sign-mismatch") : value;
  },
};

export interface TorqueTerm {
  /** Newtons, ≥ 0. */
  force: number;
  /** Metres from the pivot, ≥ 0. */
  distance: number;
  /** Radians between the lever arm and the force (π/2 for a perpendicular push). */
  angle: number;
  direction: "ccw" | "cw";
}

export interface NetTorque {
  /** Each term's signed torque, N·m (counter-clockwise positive). */
  torques: number[];
  counterclockwise: number;
  clockwise: number;
  /** Signed sum, N·m. */
  net: number;
  balanced: boolean;
}

export function netTorque(terms: readonly TorqueTerm[]): NetTorque {
  const torques = terms.map((term) => {
    const size = term.force * term.distance * Math.sin(term.angle);
    return term.direction === "ccw" ? size : -size;
  });
  const counterclockwise = torques
    .filter((value) => value > 0)
    .reduce((a, b) => a + b, 0);
  const clockwise = -torques
    .filter((value) => value < 0)
    .reduce((a, b) => a + b, 0);
  const scale = counterclockwise + clockwise;
  const net = counterclockwise - clockwise;
  return {
    torques,
    counterclockwise,
    clockwise,
    net: Math.abs(net) <= 1e-9 * scale ? 0 : net,
    balanced: Math.abs(net) <= 1e-9 * Math.max(scale, 1e-300),
  };
}

/** The force at `distance` (perpendicular to the arm) that cancels a net torque. */
export function balancingForce(net: number, distance: number): number | null {
  return distance > 0 ? Math.abs(net) / distance : null;
}

/** The distance at which a perpendicular `force` cancels a net torque. */
export function balancingDistance(net: number, force: number): number | null {
  return force > 0 ? Math.abs(net) / force : null;
}

export type Shape =
  | "disk"
  | "hoop"
  | "solidSphere"
  | "shell"
  | "rodCentre"
  | "rodEnd"
  | "plate"
  | "point";

export interface ShapeInfo {
  /** The formula as written in the working. */
  formula: string;
  /** Which dimensions the formula needs, in the order shown. */
  dimensions: readonly ("R" | "L" | "a" | "b" | "r")[];
  /** Whether the axis can be shifted with the parallel-axis theorem from a centre axis. */
  centred: boolean;
  /** The coefficient function: I from mass and dimensions in SI. */
  inertia: (
    mass: number,
    size: Record<"R" | "L" | "a" | "b" | "r", number>
  ) => number;
}

export const SHAPES: Record<Shape, ShapeInfo> = {
  disk: {
    formula: "I = ½ × M × R²",
    dimensions: ["R"],
    centred: true,
    inertia: (m, s) => 0.5 * m * s.R ** 2,
  },
  hoop: {
    formula: "I = M × R²",
    dimensions: ["R"],
    centred: true,
    inertia: (m, s) => m * s.R ** 2,
  },
  solidSphere: {
    formula: "I = ⅖ × M × R²",
    dimensions: ["R"],
    centred: true,
    inertia: (m, s) => 0.4 * m * s.R ** 2,
  },
  shell: {
    formula: "I = ⅔ × M × R²",
    dimensions: ["R"],
    centred: true,
    inertia: (m, s) => (2 / 3) * m * s.R ** 2,
  },
  rodCentre: {
    formula: "I = M × L² ÷ 12",
    dimensions: ["L"],
    centred: true,
    inertia: (m, s) => (m * s.L ** 2) / 12,
  },
  rodEnd: {
    formula: "I = M × L² ÷ 3",
    dimensions: ["L"],
    centred: false,
    inertia: (m, s) => (m * s.L ** 2) / 3,
  },
  plate: {
    formula: "I = M × (a² + b²) ÷ 12",
    dimensions: ["a", "b"],
    centred: true,
    inertia: (m, s) => (m * (s.a ** 2 + s.b ** 2)) / 12,
  },
  point: {
    formula: "I = m × r²",
    dimensions: ["r"],
    centred: false,
    inertia: (m, s) => m * s.r ** 2,
  },
};

export type InertiaError = "mass" | "size" | "offset";

/**
 * Moment of inertia of a uniform shape about its standard axis, optionally shifted by the
 * parallel-axis theorem I = I_centre + M d² (only for shapes given about their centre).
 */
export function momentOfInertia(
  shape: Shape,
  mass: number,
  size: Partial<Record<"R" | "L" | "a" | "b" | "r", number>>,
  offset = 0
): { inertia: number; centreInertia: number } | { error: InertiaError } {
  const info = SHAPES[shape];
  if (!(mass > 0)) {
    return { error: "mass" };
  }
  const full = { R: 0, L: 0, a: 0, b: 0, r: 0, ...size };
  if (info.dimensions.some((key) => !(full[key] > 0))) {
    return { error: "size" };
  }
  if (offset < 0 || (offset > 0 && !info.centred)) {
    return { error: "offset" };
  }
  const centreInertia = info.inertia(mass, full);
  return { inertia: centreInertia + mass * offset * offset, centreInertia };
}

/** α = τ / I, in rad/s². */
export function angularAcceleration(torque: number, inertia: number): number {
  return torque / inertia;
}
